import { useTextesBons } from '../bons/textes'
import { SelecteurLangue } from '../../components/SelecteurLangue'
import { useTextePublic } from '../../lib/textes-publics'
/**
 * L'enveloppe de la boutique publique — en-tête, compte à rebours, pied de page.
 *
 * ## Pourquoi le minuteur vit ICI et pas dans chaque page
 *
 * REZ-02 exige que le compte à rebours soit **visible**. Pas « visible sur
 * l'écran du panier » : visible. Un visiteur qui choisit un deuxième spectacle,
 * qui lit la présentation d'une pièce ou qui tape son adresse doit voir le temps
 * qui lui reste — c'est précisément pendant ces moments-là qu'il l'oublie.
 *
 * Le poser dans le layout, c'est aussi ce qui rend la promesse tenable sans
 * discipline : une page ajoutée demain l'aura sans que personne y pense.
 *
 * ## L'expiration : un écran, pas un message d'erreur
 *
 * Quand le panier expire, on ne se contente pas d'un toast qui disparaît. Une
 * bannière **persistante** explique ce qui s'est passé et propose la seule
 * action utile — recommencer —, parce que le visiteur peut très bien avoir la
 * tête ailleurs au moment précis où le minuteur atteint zéro.
 */

import { Suspense, useCallback } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router'

import { BandeauEcheance } from '../../components/EtatTenant'
import { cn } from '../../lib/cn'
import { CHEMINS } from '../../lib/chemins'
import { useTheatre } from '../../lib/theatre'
import { Button } from '../../ui'
import { ChargementPage } from '../../ui/Spinner'
import { useApparencePublique } from '../apparence/api'
import { CompteARebours } from './CompteARebours'
import { useAbandonnerPanier, useAccueilBoutique, usePanier } from './hooks'

export function LayoutBoutique() {
  const texteInterface = useTextePublic()
  const textesBons = useTextesBons()

  const navigate = useNavigate()
  const location = useLocation()
  /* L'écran de paiement porte son propre compte à rebours et son état de
     commande. Le panier en en-tête peut garder sa dernière valeur quelques
     instants après la confirmation et laisser croire que la vente expire. */
  const surEcranPaiement = location.pathname.startsWith('/boutique/paiement/')
  const { data: panier, refetch } = usePanier()
  const { data: accueil } = useAccueilBoutique()
  const abandonner = useAbandonnerPanier()
  /* ★ LOT B — « Spectacles » et « le guichet du théâtre » supposent une salle
     de spectacle ; un tenant `evenement` n'a ni programme ni guichet
     physique — juste sa soirée et l'adresse électronique de son organisateur
     (SPEC-BOUTIQUE §8 ter C/D). `client` par défaut : le vocabulaire complet
     reste le défaut, comme partout ailleurs dans ce lot. */
  const { type: typeTenant, nom: nomTheatre, logo } = useTheatre()
  const estEvenement = typeTenant === 'evenement'
  /* Les coordonnées que le théâtre a écrites pour son public (PER-07). */
  const mentionsPied = useApparencePublique().data?.mentionsPied ?? null

  /**
   * ★★ UN PANIER COMMANDÉ N'EST PLUS UN PANIER.
   *
   * L'en-tête n'affichait le compte à rebours et la pastille du panier qu'en
   * fonction du NOMBRE de places et des secondes restantes — jamais du statut.
   * Or `valide` (« Commandé ») garde l'un et l'autre : sur l'écran de
   * confirmation, celui qui vient de recevoir ses billets et sa facture lisait
   * encore « Places réservées encore 13:55 minutes » et un panier à 1. Autrement
   * dit, au moment exact où il faut le rassurer, on lui laissait croire que sa
   * commande allait expirer. Deux personnes sur trois recliquent.
   *
   * Seul un panier `actif` a un décompte et un contenu à défendre.
   */
  const panierActif = panier?.statut === 'actif'
  const nbPlaces = panierActif ? (panier?.nbPlaces ?? 0) : 0
  const expire = panier?.statut === 'expire'
  /* ★ LOT B — le pied de page promettait « les tarifs réduits sont
     déclaratifs » à TOUT tenant, y compris celui qui n'a que « plein » en
     vente en ligne (un événement ponctuel, par défaut — provisionnement.ts,
     amorcerTenantEvenement — ou tout théâtre qui a désactivé les siens). La
     phrase suppose l'existence d'un tarif réduit ; elle ne se lit que s'il en
     existe au moins un. `categoriesPublic` est déjà la liste vendable EN
     LIGNE (`categoriesVendablesEnLigne`) : aucun filtre à réinventer ici. */
  const aDesTarifsReduits = (accueil?.categoriesPublic ?? []).some((c) => c.code !== 'plein')

  /* Le client ne DÉCIDE jamais qu'un panier est expiré : il redemande au
     serveur, qui tranche. Un navigateur en avance de deux secondes ne doit pas
     pouvoir déclarer perdues des places encore tenues. */
  const auZero = useCallback(() => {
    void refetch()
  }, [refetch])

  const recommencer = useCallback(() => {
    abandonner.mutate(undefined, {
      onSuccess: () => {
        void navigate(CHEMINS.boutique)
      },
    })
  }, [abandonner, navigate])

  return (
    <div className="flex min-h-dvh flex-col bg-slate-50">
      {/* RESTE-A-FAIRE, lot F point A — la boutique est la seule des quatre
          coques qui ne passe pas par `EcranTenantBloquant` (surface publique,
          catalogue dégradé plutôt que blocage plein écran) ; le bandeau
          proactif s'y affiche quand même, exactement comme ailleurs. */}
      <BandeauEcheance />

      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
          <Link
            to={CHEMINS.boutique}
            className="flex items-center gap-3 text-lg font-semibold text-slate-900 hover:text-rideau-700"
          >
            {logo && (
              <img src={logo} alt={nomTheatre} className="max-h-10 max-w-32 object-contain" />
            )}
            {nomTheatre}
          </Link>

          <nav className="flex items-center gap-1 text-sm" aria-label={texteInterface('Boutique')}>
            <LienOnglet to={CHEMINS.boutique} fin>
              {estEvenement ? texteInterface('Entrées') : texteInterface('Spectacles')}
            </LienOnglet>
            {accueil?.abonnementsDisponibles === true && (
              <LienOnglet to={CHEMINS.boutiqueAbonnements}>
                {texteInterface('Abonnements')}
              </LienOnglet>
            )}
            <LienOnglet to="/boutique/bons">{textesBons('Bons cadeaux')}</LienOnglet>
          </nav>

          <SelecteurLangue className="text-slate-700" />
          {!surEcranPaiement && (
            <div className="ml-auto flex items-center gap-3">
              {/* REZ-02 — visible partout, pas seulement sur l'écran du panier. */}
              {panier !== undefined &&
                panierActif &&
                panier.secondesRestantes > 0 &&
                nbPlaces > 0 && (
                  <CompteARebours
                    secondes={panier.secondesRestantes}
                    seuilAvertissement={panier.avertissementSecondes}
                    onExpiration={auZero}
                  />
                )}

              <Link
                to={CHEMINS.boutiquePanier}
                className={cn(
                  'inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium',
                  nbPlaces > 0
                    ? 'bg-rideau-700 text-white hover:bg-rideau-800'
                    : 'text-slate-600 hover:bg-slate-100',
                )}
              >
                <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true" fill="none">
                  <path
                    d="M3 4h2l1.6 8.2a1.5 1.5 0 0 0 1.5 1.2h6.3a1.5 1.5 0 0 0 1.5-1.2L17 7H6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="8.5" cy="16.5" r="1.2" fill="currentColor" />
                  <circle cx="14.5" cy="16.5" r="1.2" fill="currentColor" />
                </svg>{' '}
                {texteInterface('Panier')}{' '}
                {nbPlaces > 0 && (
                  <span className="rounded-full bg-white/25 px-1.5 text-xs tabular-nums">
                    {nbPlaces}
                  </span>
                )}
              </Link>
            </div>
          )}
        </div>

        {/* ★ REZ-04 vu du visiteur. Persistant, pas un toast : l'expiration
            arrive souvent quand on regarde ailleurs. */}
        {expire && !surEcranPaiement && (
          <div className="border-t border-red-200 bg-red-50">
            <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-4 py-3 text-sm text-red-900">
              <span className="font-medium">{texteInterface('Votre panier a expiré.')}</span>
              <span>
                {' '}
                {texteInterface(
                  'Les places que vous aviez retenues ont été remises en vente. Elles sont peut-être encore libres : il suffit de les choisir à nouveau.',
                )}{' '}
              </span>
              <Button
                variante="danger"
                taille="sm"
                className="ml-auto"
                chargement={abandonner.isPending}
                onClick={recommencer}
              >
                {' '}
                {texteInterface('Choisir de nouvelles places')}{' '}
              </Button>
            </div>
          </div>
        )}
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6">
        <Suspense fallback={<ChargementPage />}>
          <Outlet />
        </Suspense>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-6 text-xs text-slate-500">
          <p>
            {nomTheatre} {texteInterface('— billetterie en ligne.')}{' '}
            {aDesTarifsReduits && (
              <>
                {' '}
                {texteInterface(
                  'Les tarifs réduits sont déclaratifs : le justificatif est contrôlé à l’entrée de la salle.',
                )}{' '}
              </>
            )}
          </p>
          {/* ★★ « CONTACTEZ DIRECTEMENT LE GUICHET » — SANS DONNER DE CONTACT.
              Le pied invitait à joindre le théâtre et ne portait ni téléphone,
              ni adresse, ni courriel : le champ « Mentions de bas de page »
              (Apparence) existe, est rendu publiquement par
              `GET /apparence/publique`, et n'était affiché nulle part côté
              public. On l'affiche — et, tant qu'il est vide, on cesse de
              promettre un contact qu'on ne donne pas : l'invitation ne
              s'affiche QUE si le théâtre a effectivement écrit comment on le
              joint. Une phrase en moins vaut mieux qu'une impasse. */}
          <p className="mt-1">
            {estEvenement ? (
              <>
                {texteInterface(
                  'Une question sur cette soirée ? Contactez directement l’organisateur.',
                )}
              </>
            ) : (
              <>
                {' '}
                {texteInterface(
                  'Une question, un groupe, un tarif particulier ? Contactez directement le guichet du théâtre.',
                )}{' '}
              </>
            )}
          </p>
          {/* ★★ ET VOICI COMMENT — ce qui manquait.
              L'invitation ci-dessus existait seule : ni téléphone, ni adresse,
              ni courriel nulle part dans la boutique. Le champ « Mentions de
              bas de page » (Apparence) porte exactement cela, est servi
              publiquement par `GET /apparence/publique`, et n'était affiché
              nulle part côté public. Il l'est ici. Tant qu'il est vide, la
              boutique reste muette sur le « comment » — l'écran Apparence le
              dit maintenant à celui qui le remplit. */}
          {mentionsPied !== null && mentionsPied.trim() !== '' && (
            <p className="mt-2 whitespace-pre-line text-slate-600">{mentionsPied}</p>
          )}
        </div>
      </footer>
    </div>
  )
}

function LienOnglet({
  to,
  fin = false,
  children,
}: {
  readonly to: string
  readonly fin?: boolean
  readonly children: React.ReactNode
}) {
  return (
    <NavLink
      to={to}
      end={fin}
      className={({ isActive }) =>
        cn(
          'rounded-md px-3 py-1.5 font-medium',
          isActive ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50',
        )
      }
    >
      {children}
    </NavLink>
  )
}
